import { App as AntApp, ConfigProvider } from "antd";
import enGB from "antd/locale/en_GB";
import zhCN from "antd/locale/zh_CN";
import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { I18nextProvider, useTranslation } from "react-i18next";
import i18n from "../i18n";
import { ThemeProvider, useTheme } from "../hooks/useTheme";
import { createThemeConfig } from "./tokens";
import { SelectionModeProvider } from "../hooks/useSelectionMode";
import { useReducedMotion } from "../hooks/useReducedMotion";

function AntDesignBridge({ children }: { children: ReactNode }) {
  const { theme } = useTheme();
  const reducedMotion = useReducedMotion();
  const { i18n: i18nInstance } = useTranslation();
  const language = i18nInstance.resolvedLanguage === "en-GB" ? "en-GB" : "zh-CN";
  const config = useMemo(() => createThemeConfig(theme, reducedMotion), [theme, reducedMotion]);
  // Antd 首次遇到与父级不同的 motion 才插入 MotionWrapper 的 Provider。
  // 父级固定为初始配置的反值，使内层从首次渲染就有稳定的包裹层，切换时不重挂业务子树。
  const [motionBoundaryTheme] = useState(() => ({ token: { motion: config.token?.motion === false } }));

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  return (
    <ConfigProvider theme={motionBoundaryTheme}>
      <ConfigProvider
        locale={language === "en-GB" ? enGB : zhCN}
        theme={config}
        button={{ autoInsertSpace: false }}
      >
        <AntApp>{children}</AntApp>
      </ConfigProvider>
    </ConfigProvider>
  );
}

export function DesignSystemProvider({ children }: { children: ReactNode }) {
  return (
    <I18nextProvider i18n={i18n}>
      <ThemeProvider>
        <SelectionModeProvider><AntDesignBridge>{children}</AntDesignBridge></SelectionModeProvider>
      </ThemeProvider>
    </I18nextProvider>
  );
}
